import React, { useState, useEffect } from 'react';
import { listUsers } from '../api/admin';
import AdminUserDetail from './admin/AdminUserDetail';
import CreateUserModal from './admin/CreateUserModal';
import { getCountryFlag } from '../util/storage';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const offset = page * limit;
      const data = await listUsers(limit, offset);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, limit]);

  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  const handleUserCreated = () => {
    setShowCreateModal(false);
    loadUsers(); // Refresh list
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
  };

  const handleCloseDetail = () => {
    setSelectedUser(null);
  };

  const handleUserUpdated = () => {
    loadUsers(); // Refresh list after update
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  if (error) {
    return (
      <div className="admin-users">
        <h2>User Management</h2>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <div>
          <h2>User Management</h2>
          <p className="user-count">{total} total users</p>
        </div>
        <button className="btn-primary" onClick={handleCreateUser}>
          + Create User
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Country</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.username}</strong>
                </td>
                <td>{user.full_name}</td>
                <td>{user.email}</td>
                <td>
                  <span className="country-display">
                    <span className="country-flag-table">{getCountryFlag(user.country_code)}</span>
                    <span className="country-code">{user.country_code || '—'}</span>
                  </span>
                </td>
                <td>
                  <div className="role-badges">
                    {user.roles.map((role) => (
                      <span key={role} className={`badge role-${role}`}>
                        {role}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`badge status-${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-secondary btn-small"
                    onClick={() => handleViewUser(user)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
          </button>
        </div>
      )}

      {selectedUser && (
        <AdminUserDetail
          user={selectedUser}
          onClose={handleCloseDetail}
          onUpdate={handleUserUpdated}
        />
      )}

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleUserCreated}
        />
      )}
    </div>
  );
}
